import type { LoaderFunctionArgs } from 'react-router-dom';
import Client_, { Paste, PasteComment } from '../../api/Client';

export type PasteLoaderData = {
  paste: Paste;
  body: string;
  can_edit: boolean;
  can_comment: boolean;
  comments: PasteComment[];
} | null;

// A prefetch older than this can hold text that somebody changed since.
const PREFETCH_MAX_AGE_MS = 10_000;

const pending = new Map<string, { at: number; data: Promise<PasteLoaderData> }>();
const saving = new Map<string, Promise<unknown>>();

/** A read waits for this save and drops a prefetch that holds the old text. */
export function trackSave(pasteId: string, save: Promise<unknown>) {
  pending.delete(pasteId);
  saving.set(pasteId, save);
  void save.finally(() => {
    if (saving.get(pasteId) === save) saving.delete(pasteId);
  });
}

async function readAfterSave(pasteId: string) {
  await saving.get(pasteId)?.catch(() => undefined);
  return fetchPaste(pasteId);
}

async function fetchPaste(pasteId: string): Promise<PasteLoaderData> {
  const { err, res } = await Client_.client_().getPaste(pasteId);
  const result = res?.result;
  if (err || !result?.paste) return null;
  return {
    paste: result.paste,
    body: result.body ?? '',
    can_edit: result.can_edit === true,
    can_comment: result.can_comment === true,
    comments: Array.isArray(result.comments) ? result.comments : [],
  };
}

/** Start the read before the click lands, so the loader finds it in flight. */
export function prefetchPaste(pasteId: string) {
  const current = pending.get(pasteId);
  if (current && Date.now() - current.at < PREFETCH_MAX_AGE_MS) return;
  pending.set(pasteId, { at: Date.now(), data: readAfterSave(pasteId) });
}

/** A paste the client just created is empty, so the loader needs no read. */
export function seedPaste(paste: Paste) {
  pending.set(paste.paste_id, {
    at: Date.now(),
    data: Promise.resolve({ paste, body: '', can_edit: true, can_comment: true, comments: [] }),
  });
}

/** Fetch the paste in parallel with the route chunk. */
export async function pasteLoader({ params }: LoaderFunctionArgs): Promise<PasteLoaderData> {
  const pasteId = params.pasteId;
  if (!pasteId) return null;
  const prefetched = pending.get(pasteId);
  pending.delete(pasteId);
  if (prefetched && Date.now() - prefetched.at < PREFETCH_MAX_AGE_MS) {
    const data = await prefetched.data;
    if (data) return data;
  }
  return readAfterSave(pasteId);
}
